import "./ProfileModal.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field, Form, Formik, getIn } from "formik";
import { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { object, string } from "yup";

import { PasswordChange, User } from "pwapi";

import {
  logout,
  saveProfile,
  userMe,
  resetSaveProfileErrors,
  userUnsetMFA,
} from "shared/actions/authActions";
import { wipeCaseDetail } from "shared/actions/caseDetailActions";
import { wipeCases } from "shared/actions/casesActions";
import { wipeSlideDetail } from "shared/actions/slideDetailActions";

import { PathologyWatchButton as Button } from "components/customInputs/buttons/Button";
import PhoneInput from "components/customInputs/PhoneInput";
import Spinner from "components/Spinner";
import ApplicationState from "store/ApplicationState";
import { AppDispatch } from "store/store";

import TextInput from "./customInputs/TextInput";
import LinkButton from "./LinkButton";
import MfaMethodSelectionForm from "./MfaMethodSelectionForm";
import PasswordChangeFormFields, {
  passwordMatchSchema,
  PasswordRequirements,
} from "./PasswordChangeFormFields";

const ProfileModal = () => {
  const user: User | undefined = useSelector(
    ({ auth }: ApplicationState) => auth.user
  );
  const loadingUser = useSelector(
    ({ requestStatuses }: ApplicationState) =>
      requestStatuses.loading[userMe.getRequestKey()]
  );
  const updateErrors = useSelector(
    ({ requestStatuses }: ApplicationState) =>
      requestStatuses.errors["user/me/update"]
  );
  const mfaEnabled = !!user?.userprofile?.mfa_method;

  // This yup schema is passed as Formik's validationSchema property
  // Since new_password1 and new_password2 fields can be left blank when
  // changing first name, last_name, and email, they cannot be required()
  const schema = object().shape({
    email: string().required(),
    first_name: string().required(),
    last_name: string().required(),
    ...passwordMatchSchema(false),
  });

  const dispatch = useDispatch<AppDispatch>();

  const [openModal, setOpenModal] = useState("");

  // Extends User to have new_password1 and new_password2 fields
  type ProfileFormData = User & PasswordChange;

  const handleSave = (data: ProfileFormData) => {
    const { new_password1, new_password2, ...newUserValues } = data;
    dispatch(
      saveProfile(newUserValues, { new_password1, new_password2 }, () => {
        setOpenModal("");
      })
    );
  };

  // Load User when opening Modal (just in case changes have been made in another session)
  const loadedUserRef = useRef(false);
  useEffect(() => {
    if (!openModal && loadedUserRef.current) loadedUserRef.current = false;
    if (user?.id && openModal && !loadingUser && !loadedUserRef.current) {
      dispatch(userMe());
      loadedUserRef.current = true;
    }
  }, [user?.id, openModal, loadingUser]);

  // Reset Request Errors when closing modal
  useEffect(() => {
    if (updateErrors && openModal !== "profile") {
      dispatch(resetSaveProfileErrors());
    }
  }, [updateErrors, openModal]);

  if (!user) return null;

  return (
    <div>
      <LinkButton
        onClick={() => {
          setOpenModal("profile");
        }}
        className="text-muted"
        style={{ textDecoration: "none" }}
      >
        {user.first_name || user.last_name ? (
          <span>
            {user.first_name}&nbsp;{user.last_name}
          </span>
        ) : (
          <span>{user.username}</span>
        )}
        &nbsp;
        <FontAwesomeIcon icon="user" />
      </LinkButton>

      <Modal
        isOpen={openModal === "profile"}
        onRequestClose={() => {
          setOpenModal("");
        }}
        contentLabel="User Settings"
        className="Modal ProfileModal"
        overlayClassName="Overlay"
      >
        <h3>Profile &amp; Settings</h3>
        {loadingUser ? (
          <div
            style={{
              minHeight: "803px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Spinner />
          </div>
        ) : (
          <Formik
            initialValues={{
              ...user,
              new_password1: "",
              new_password2: "",
            }}
            validationSchema={schema}
            onSubmit={handleSave}
          >
            {({ handleChange, errors, touched }) => {
              const onChange = (e: any) => {
                if (getIn(updateErrors, e.target.name || e.target.id)) {
                  dispatch(
                    resetSaveProfileErrors(e.target.name || e.target.id)
                  );
                }
                handleChange(e);
              };
              return (
                <Form id="ProfileModalForm">
                  <div className="Settings__profile">
                    {/*
                      Cannot allow edits to username for now; if a new username
                      arrives in the request body of the PATCH, the back end
                      applies the change so swiftly that the PATCH response is
                      401 Unauthorized since you're now logged in with a stale username.
                      You would think this wouldn't matter until the next login
                      but it shuts down all communication with the server immediately.
                      */}
                    <div className="CustomInput CustomInput__container CustomInput--readonly">
                      <div className="inputLabel__container">
                        <label>Username</label>
                      </div>
                      <strong>{user.username}</strong>
                    </div>
                    <Field name="userprofile.title">
                      {({ field }: any) => (
                        <TextInput
                          label="Title"
                          id="title"
                          {...field}
                          onChange={onChange}
                        />
                      )}
                    </Field>
                    <Field name="first_name">
                      {({ field }: any) => (
                        <TextInput
                          required
                          label="First Name"
                          id="first_name"
                          {...field}
                          onChange={onChange}
                        />
                      )}
                    </Field>
                    <Field name="last_name">
                      {({ field }: any) => (
                        <TextInput
                          required
                          label="Last Name"
                          id="last_name"
                          {...field}
                          onChange={onChange}
                        />
                      )}
                    </Field>
                    <Field name="email">
                      {({ field }: any) => (
                        <TextInput
                          required
                          label={`Email${
                            user.userprofile?.mfa_method === "email" ? "*" : ""
                          }`}
                          id="email"
                          type="email"
                          {...field}
                          onChange={onChange}
                          error={updateErrors?.email || getIn(errors, "email")}
                          disabled={user.userprofile?.mfa_method === "email"}
                        />
                      )}
                    </Field>
                    <Field name="userprofile.phone">
                      {({ field }: any) => (
                        <PhoneInput
                          required
                          label={`Phone${
                            user.userprofile?.mfa_method === "sms" ? "*" : ""
                          }`}
                          id="phone"
                          {...field}
                          onChange={onChange}
                          error={
                            updateErrors?.userprofile?.phone ||
                            getIn(errors, "userprofile.phone")
                          }
                          disabled={user.userprofile?.mfa_method === "sms"}
                        />
                      )}
                    </Field>
                  </div>

                  {user.userprofile?.mfa_method &&
                    ["email", "sms"].includes(user.userprofile?.mfa_method) && (
                      <div className="mt-2">
                        <em>
                          <small>
                            *Cannot change
                            {user.userprofile.mfa_method === "sms"
                              ? " phone while SMS "
                              : " email while it "}
                            is selected as authentication method
                          </small>
                        </em>
                      </div>
                    )}

                  <div>
                    <div>
                      <hr />
                    </div>
                    <h4>Change Password</h4>
                    <div className="Settings__profile">
                      <PasswordChangeFormFields
                        labels
                        handleChange={onChange}
                        errors={errors}
                        touched={touched}
                      />
                    </div>
                  </div>
                  <div className="PasswordRequirements">
                    <PasswordRequirements />
                  </div>
                  <div>
                    <hr />
                  </div>
                  <div>
                    <h4>Authentication Method</h4>
                    <div className="my-3">
                      Current:{" "}
                      <strong>
                        {user?.userprofile?.mfa_method
                          ? {
                              email: "Email",
                              sms: "SMS",
                              app: "Authenticator App",
                            }[user.userprofile.mfa_method]
                          : "None"}
                      </strong>
                    </div>
                    <Button onClick={() => setOpenModal("authentication")}>
                      Change Authentication Method
                    </Button>
                    {mfaEnabled && (
                      <Button
                        className="ml-2"
                        onClick={() =>
                          user.id && dispatch(userUnsetMFA(user.id))
                        }
                      >
                        Remove Authentication Method
                      </Button>
                    )}
                  </div>
                  <div>
                    <hr />
                  </div>
                  <footer className="Modal__footer">
                    <Link
                      to="/"
                      onClick={() => {
                        setOpenModal("");
                        dispatch(logout());
                        dispatch(wipeCases());
                        dispatch(wipeCaseDetail());
                        dispatch(wipeSlideDetail());
                      }}
                    >
                      <span className="NonButton NonButton--red">Log Out</span>
                    </Link>
                    <div className="PageLayout Modal__footer__buttons">
                      <button
                        type="button"
                        title="Close"
                        className="Button Button--blue NonButton"
                        onClick={() => {
                          setOpenModal("");
                        }}
                      >
                        Cancel
                      </button>{" "}
                      <button
                        type="submit"
                        title="Save"
                        className="Button Button--blue"
                      >
                        Save
                      </button>
                    </div>
                  </footer>
                </Form>
              );
            }}
          </Formik>
        )}
      </Modal>

      <Modal
        isOpen={openModal === "authentication"}
        onRequestClose={() => {
          setOpenModal("");
        }}
        contentLabel="Change Authentication Method"
        className="Modal ProfileModal"
        overlayClassName="Overlay"
      >
        <h3 className="mb-4">Change Authentication Method</h3>
        <MfaMethodSelectionForm
          onCancel={() => setOpenModal("profile")}
          onConfirm={() => setOpenModal("profile")}
        />
      </Modal>
    </div>
  );
};

export default ProfileModal;
